import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { api, setAuthToken, loadStoredToken } from '@/lib/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickCheck, setQuickCheck] = useState({ recognized: false });

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch (e) {
      setUser(null);
      setAuthToken(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      const token = loadStoredToken();
      // If the user is already authenticated (token in storage), we ONLY
      // need /auth/me to validate it. /auth/quick-check is meaningful
      // only on the login screen (to offer "quick login" by recognised
      // IP), so we skip it here and save a round-trip — crucial when
      // the backend has a cold-start delay.
      if (token) {
        // 8s hard cap on the boot validation. If the backend is in cold
        // start the user would otherwise stare at "Caricamento..." for
        // 30+ seconds. After 8s we just release the UI: the API will
        // catch up in the background and refreshMe re-fires on its own.
        const timeout = new Promise(resolve => setTimeout(resolve, 8000));
        await Promise.race([refreshMe().catch(() => null), timeout]);
        setLoading(false);
        return;
      }
      // Unauthenticated boot — release the UI to the login screen
      // IMMEDIATELY so the user can start typing while quick-check
      // resolves in the background.
      setLoading(false);
      api.get('/auth/quick-check', { timeout: 8000 })
        .then(r => setQuickCheck(r.data || { recognized: false }))
        .catch(() => { /* best-effort: stay on full login form */ });
    })();
  }, [refreshMe]);

  // Keep-alive: ping a lightweight DB-less health endpoint every 4 minutes
  // and immediately when the tab regains focus. Production hosts often
  // scale containers to zero after ~5 minutes of idle, causing a 30s cold
  // start for whoever opens the app next. This keeps the backend warm
  // during business hours without any noticeable load.
  useEffect(() => {
    const ping = () => { api.get('/healthz', { timeout: 4000 }).catch(() => {}); };
    ping(); // immediate ping on mount
    const interval = setInterval(ping, 4 * 60 * 1000);
    const onFocus = () => ping();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, []);

  // Handle SESSION_REVOKED globally (force logout, multi-device kicked in, ...)
  useEffect(() => {
    const onRevoked = (e) => {
      const msg = e?.detail?.message || 'Sessione terminata. Effettua di nuovo l\'accesso.';
      setAuthToken(null);
      setUser(null);
      toast.error(msg);
      // Force redirect to login if not already there
      try {
        const path = window.location.pathname;
        if (path !== '/login') window.location.href = '/login';
      } catch (err) { /* ignore */ }
    };
    window.addEventListener('pc-session-revoked', onRevoked);
    return () => window.removeEventListener('pc-session-revoked', onRevoked);
  }, []);

  const login = async (email, password, remember = true) => {
    try {
      const { data } = await api.post('/auth/login', { email, password, remember_ip: remember });
      setAuthToken(data.token);
      setUser(data.user);
      return data;
    } catch (err) {
      // Decorate DEVICE_LOCKED / MULTI_DEVICE_LIMIT with a parsed message
      // so the toast su LoginPage può mostrarli con durata estesa.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 409 && typeof detail === 'string') {
        if (detail.startsWith('DEVICE_LOCKED')) {
          const msg = detail.replace(/^DEVICE_LOCKED:?\s*/, '');
          const e2 = new Error(msg);
          e2.code = 'DEVICE_LOCKED';
          e2.userMessage = msg;
          throw e2;
        }
        if (detail.startsWith('MULTI_DEVICE_LIMIT')) {
          const msg = detail.replace(/^MULTI_DEVICE_LIMIT:?\s*/, '');
          const e2 = new Error(msg);
          e2.code = 'DEVICE_LOCKED'; // riusiamo lo stesso code per ottenere toast lungo su LoginPage
          e2.userMessage = msg;
          throw e2;
        }
      }
      throw err;
    }
  };

  const quickLogin = async () => {
    try {
      const { data } = await api.post('/auth/quick-login');
      setAuthToken(data.token);
      setUser(data.user);
      return data;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 409 && typeof detail === 'string') {
        if (detail.startsWith('DEVICE_LOCKED')) {
          const msg = detail.replace(/^DEVICE_LOCKED:?\s*/, '');
          const e2 = new Error(msg);
          e2.code = 'DEVICE_LOCKED';
          e2.userMessage = msg;
          throw e2;
        }
        if (detail.startsWith('MULTI_DEVICE_LIMIT')) {
          const msg = detail.replace(/^MULTI_DEVICE_LIMIT:?\s*/, '');
          const e2 = new Error(msg);
          e2.code = 'DEVICE_LOCKED';
          e2.userMessage = msg;
          throw e2;
        }
      }
      throw err;
    }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) { /* logout is best-effort */ }
    setAuthToken(null);
    setUser(null);
  };

  // Memoize the context value so consumers don't re-render on each parent
  // render. Without this, every render of AuthProvider creates a fresh object
  // reference, which would propagate updates to every consumer that reads
  // from useAuth() unnecessarily.
  const value = useMemo(
    () => ({ user, setUser, loading, login, quickLogin, logout, quickCheck, refreshMe }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
